import React, { useMemo } from 'react';
import {
  ReactFlow,
  MarkerType,
  useNodesState,
  useEdgesState,
  ConnectionMode,
  Handle,
  Position,
  type Node,
  type Edge,
} from 'reactflow';
import 'reactflow/dist/style.css';
import { SourceIcon } from './SourceIcon';
import type { DependencyGraph } from '../services/analysis.service';
import { cn } from '@/lib/utils';
import { autoLayout } from '../services/diagram.service';
import { NodeCard, getNodeColor, getNodeIcon } from './NodeCard';

interface AnalysisDependencyGraphProps {
  graph: DependencyGraph;
  className?: string;
  animated?: boolean; // Whether edges should be animated
}

// Custom node component using shared NodeCard
const CustomNode = ({ data }: { data: any }) => {
  const { type, title, sourceId, pipelineNodeId } = data;

  const nodeWidth = 270;
  const nodeHeight = 60;

  const nodeColor = getNodeColor(type);

  // For source nodes, use SourceIcon as custom icon
  let customIcon: React.ReactNode | undefined;
  if (type === 'source') {
    let sourceIdToUse = sourceId;
    if (!sourceIdToUse) {
      if (title === 'Flat Files') {
        sourceIdToUse = 'flat_files';
      } else if (title === 'Snowflake') {
        sourceIdToUse = 'snowflake';
      } else {
        const extractedPipelineNodeId = pipelineNodeId || (data.nodeId ? data.nodeId.split(':')[1] : undefined);
        sourceIdToUse = extractedPipelineNodeId || title;
      }
    }
    customIcon = <SourceIcon sourceId={sourceIdToUse} size={24} />;
  } else {
    customIcon = getNodeIcon(type, nodeColor, 'md');
  }

  // For report type, override title to "Ellie"
  const displayTitle = type === 'report' ? 'Ellie' : title;

  // Determine which handles to show based on type
  const showTargetHandle = type !== 'source';
  const showSourceHandle = type !== 'report';

  return (
    <NodeCard
      title={displayTitle}
      nodeType={type}
      width={nodeWidth}
      height={nodeHeight}
      customIcon={customIcon}
      iconSize="md"
    >
      {showTargetHandle && <Handle type="target" position={Position.Left} />}
      {showSourceHandle && <Handle type="source" position={Position.Right} />}
    </NodeCard>
  );
};

const nodeTypes = {
  custom: CustomNode,
};

// Layout algorithm: use linear auto layout from diagram service
const calculateLayout = (nodes: DependencyGraph['nodes'], edges: DependencyGraph['edges']) => {
  // First, create basic Node and Edge structures for autoLayout
  const reactFlowNodes: Node[] = nodes.map((node) => {
    // Extract pipeline_node_id for source nodes (format: source:pipeline_node_id)
    let sourceId: string | undefined;
    let pipelineNodeId: string | undefined;
    
    if (node.type === 'source') {
      const parts = node.id.split(':');
      if (parts.length >= 2 && parts[0] === 'source') {
        pipelineNodeId = parts[1];
      }
      // Handle special cases where title matches known source IDs
      if (node.title === 'Flat Files') {
        sourceId = 'flat_files';
      } else if (node.title === 'Snowflake') {
        sourceId = 'snowflake';
      }
    }

    return {
      id: node.id,
      type: 'custom',
      position: { x: 0, y: 0 }, // Will be set by autoLayout
      data: {
        type: node.type,
        title: node.title,
        sourceId,
        pipelineNodeId, // Store pipeline_node_id for linking
        nodeId: node.id,
      },
    };
  });

  const reactFlowEdges: Edge[] = edges.map((edge) => ({
    id: edge.id || `${edge.from_id}-${edge.to_id}`,
    source: edge.from_id,
    target: edge.to_id,
  }));

  // Use linear auto layout with reduced spacing
  // Node size is 270x60 (rounded rectangles, 50% wider)
  const nodePositions = autoLayout(reactFlowNodes, reactFlowEdges, 'LINEAR', 270, 60);

  // Apply positions to nodes
  const positionedNodes = reactFlowNodes.map((node) => ({
    ...node,
    position: nodePositions[node.id] || { x: 0, y: 0 },
  }));

  return positionedNodes;
};

export const AnalysisDependencyGraph: React.FC<AnalysisDependencyGraphProps> = ({
  graph,
  className,
  animated = true,
}) => {
  // Log the incoming graph data
  console.log('AnalysisDependencyGraph - Received graph:', graph);
  console.log('AnalysisDependencyGraph - Nodes:', graph.nodes);
  console.log('AnalysisDependencyGraph - Edges:', graph.edges);

  // Calculate layout
  const initialNodes = useMemo(() => {
    const calculatedNodes = calculateLayout(graph.nodes, graph.edges);
    console.log('AnalysisDependencyGraph - Calculated initial nodes:', calculatedNodes);
    return calculatedNodes;
  }, [graph]);

  // Convert edges to react-flow format with optional animation
  const initialEdges: Edge[] = useMemo(
    () => {
      const convertedEdges = graph.edges.map((edge) => ({
        id: edge.id || `${edge.from_id}-${edge.to_id}`,
        source: edge.from_id,
        target: edge.to_id,
        type: 'smoothstep',
        animated: animated,
        markerEnd: {
          type: MarkerType.ArrowClosed,
          color: '#64748b',
        },
        style: {
          stroke: '#64748b',
          strokeWidth: 2,
        },
      }));
      console.log('AnalysisDependencyGraph - Converted initial edges:', convertedEdges);
      return convertedEdges;
    },
    [graph.edges, animated]
  );

  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges);

  console.log('AnalysisDependencyGraph - React Flow nodes state:', nodes);
  console.log('AnalysisDependencyGraph - React Flow edges state:', edges);

  // Update nodes/edges when graph changes
  React.useEffect(() => {
    console.log('AnalysisDependencyGraph - useEffect triggered, graph changed:', graph);
    const newNodes = calculateLayout(graph.nodes, graph.edges);
    console.log('AnalysisDependencyGraph - New nodes calculated:', newNodes);
    setNodes(newNodes);
    
    const newEdges: Edge[] = graph.edges.map((edge) => ({
      id: edge.id || `${edge.from_id}-${edge.to_id}`,
      source: edge.from_id,
      target: edge.to_id,
      type: 'smoothstep',
      animated: animated,
      markerEnd: {
        type: MarkerType.ArrowClosed,
        color: '#64748b',
      },
      style: {
        stroke: '#64748b',
        strokeWidth: 2,
      },
    }));
    console.log('AnalysisDependencyGraph - New edges calculated:', newEdges);
    setEdges(newEdges);
  }, [graph, setNodes, setEdges, animated]);

  return (
    <div className={cn('w-full min-h-[400px] relative', className)}>
      <div className="absolute inset-0">
      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        nodeTypes={nodeTypes}
        connectionMode={ConnectionMode.Loose}
        fitView
        fitViewOptions={{ padding: 0.2 }}
        minZoom={0.1}
        maxZoom={2}
        nodesDraggable={false}
        proOptions={{hideAttribution: true}}
      >
      </ReactFlow>
      </div>
    </div>
  );
};

